import { useMemo } from 'react';

import { useHistory } from 'react-router-dom';

import { useEvent } from 'common/components/hooks/useEvent/useEvent';
import { useMutationSubmitCallback } from 'common/components/hooks/useMutationSubmitCallback/useMutationSubmitCallback';
import { dismissTask } from 'common/components/TasksMenu/TaskItem/mutations';
import type { TaskItemProps } from 'common/components/TasksMenu/TaskItem/TaskItem';
import { TaskStatus } from 'common/components/TasksWidget/queries';
import { AppRoutes, createRouteFromParams } from 'routes';

export type UseTaskItemOutput = ReturnType<typeof useTaskItem>;

export default function useTaskItem(props: TaskItemProps) {
  const history = useHistory();

  const {
    onSubmit: submitDismiss,
    mutationResult: { loading: dismissLoading },
  } = useMutationSubmitCallback({
    mutation: dismissTask,
    variables: { id: props.task.id },
    onSuccess: result => {
      if (result?.dismissTask) {
        props.onTaskDismiss(result.dismissTask);
      }
    },
    errorMessage: 'An error occurred during task dismissal.',
  });

  const handleDismiss = useEvent(() => submitDismiss());

  const goToTaskInfo = useEvent(() =>
    history.push(
      createRouteFromParams(AppRoutes.TASK, { taskId: props.task.id }).concat(`?url=${history.location.pathname}`)
    )
  );

  const taskStatus = props.task.status;
  const showDismissButton = ![TaskStatus.RUNNING, TaskStatus.NOT_STARTED, TaskStatus.CANCELLING].includes(taskStatus);

  const owner = useMemo(
    () => ({
      account: props.task.account,
      organization: props.task.organization,
      customer: props.task.customer,
    }),
    [props.task]
  );

  return {
    showDismissButton,
    handleDismiss,
    goToTaskInfo,
    dismissLoading,
    owner,
  };
}
